
import { expandToSquare, boundsToUnitQuad } from './MinimapMath.js';

const av = Autodesk.Viewing;

// Use the largest we can get from DS
const DsThumbnailSize = 400;
const GeneratedThumbnailSize = 1500;

const getThumbnailCropRegion = (sheetBox, viewport, sheetUnitScale = 0.0254) => {

    // Thumbnails are square and contain empty pixels to expand the rectangle to a square.
    // Therefore, the whole thumbnail image does not match the sheet bounds exactly,
    // but actually corresponds to the square containing it.
    expandToSquare(sheetBox);

    // Actually, we shouldn't care about sheetUnitScale here. We only
    // need it because Revit extractor gives us viewport bounds and sheet bounds in different coordinate systems.
    // Unfortunately, we have to guess the sheetUnitScale here, because we cannot load all sheets first just to get the unitScale.
    //
    // TODO: If we provide the viewport boundary in sheet coordinates in Revit extractor, we don't need this hack anymore.

    // get viewport bounds in sheet coords (usually inches)
    var viewportBounds = viewport.getViewportBounds(sheetUnitScale).clone();

    // map from sheetBox to thumbnail pixels
    boundsToUnitQuad(viewportBounds.min, sheetBox);
    boundsToUnitQuad(viewportBounds.max, sheetBox);

    return viewportBounds;
};

// Applies css styling on image element, so that the given cropRegion fits the parent div
//
// @param {Box2}  cropRegion - in [0,1]^2, relative to the full image
// @param {Image} image      - html image element
const cropImage = (image, cropRegion) => {
    const _document = av.getGlobal().document;
    const canvas = _document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // compute size of crop region (relative to full image)
    const cropWidthRatio  = cropRegion.max.x - cropRegion.min.x;
    const cropHeightRatio = cropRegion.max.y - cropRegion.min.y;

    const destWidth = cropWidthRatio * image.width;
    const destHeight = cropHeightRatio * image.height;

    canvas.width = destWidth;
    canvas.height = destHeight;

    ctx.drawImage(
        image,
        cropRegion.min.x * image.width, (1 - cropRegion.max.y) * image.height,
        destWidth, destHeight,
        0, 0,
        destWidth, destHeight
    );

    const texImage = new Image();
    texImage.src = canvas.toDataURL();

    return texImage;
};

// Sheetmap thumbnails are embedded in the minimap to display for which regions inside the minimap a sheetmap is available.
// A sheetmap thumbnail is a sheet thumbnail that is cropped down to a single viewport and embedded into the main map.
export default class SheetMapThumbnail {

    // @param {SheetMapConfig} sheetMapConfig - Config object for the sheet to be represented by this thumbnail
    constructor(sheetMapConfig, viewer, onThumbnailReady) {
        this.sheetMapConfig = sheetMapConfig;

        this.viewer = viewer;

        this.thumbnailImage = null;

        this.thumbnailRefined = false;

        this.onThumbnailReady = onThumbnailReady;

        this.img = new Image();
        this.img.onload = async () => {
            const sheetNode = await this.sheetMapConfig.getSheetNode();

            // get full sheet bounds from viewbox
            const viewNode = sheetNode.search({ type: 'view' })[0];

            // We can't crop the thumbnail without knowing the sheet's original dimensions.
            // The problem is that the thumbnail is always square - and the sheet isn't.
            // Even if we know the exact dimensions of the viewport - it's not enough in order to decide how to crop the squared thumbnail.
            if (!viewNode) {
                return;
            }

            var viewBoxValues = viewNode.getViewBox();
            var sheetBox = new THREE.Box2();
            sheetBox.min.set(viewBoxValues[0], viewBoxValues[1]);
            sheetBox.max.set(viewBoxValues[2], viewBoxValues[3]);
            const cropRegion = getThumbnailCropRegion(sheetBox, this.sheetMapConfig.viewport);

            this.cropThumbnail(this.img, cropRegion);
        };
    }

    cropThumbnail(img, cropRegion) {
        const croppedThumbnail = cropImage(img, cropRegion);

        croppedThumbnail.onload = () => {
            this.thumbnailImage = croppedThumbnail;
            this.onThumbnailReady(this.sheetMapConfig);
        };
    }

    onThumbnailDownloaded(blobUrl) {
        // Verify that the thumbnail wasn't generated locally meanwhile.
        if (!this.thumbnailRefined) {
            this.img.src = blobUrl;
        }
    }

    // Creates a div containing the thumbnail of a given sheet node as an image element.
    async requestThumbnail() {

        const sheetNode = await this.sheetMapConfig.getSheetNode();

        // get thumbnail url
        const root = sheetNode.getRootNode();
        const doc  = root.getDocument();

        const options = doc.getThumbnailOptions(sheetNode.data, DsThumbnailSize, DsThumbnailSize);

        // takes care of local bubbles
        if (sheetNode.data.thumbnailUrn) {
            options.urn = sheetNode.getDocument().getFullPath(sheetNode.data.thumbnailUrn);
            options.responseType = 'blob';
            Autodesk.Viewing.Private.ViewingService.getThumbnail(Autodesk.Viewing.initLoadContext(), options.urn, (response) => {
                var reader = new FileReader();
                reader.onload = (e) => {
                    this.onThumbnailDownloaded(e.target.result);
                };
                reader.readAsDataURL(response);

            },
            (error) => { console.warn(`Failed to load sheet thumbnail. Error: ${error}, Urn: ${options.urn}, SheetName: ${sheetNode.name()}`); },
            options);
        } else if (Autodesk.Viewing.endpoint.getUseCookie()) {
            // Set derivative service Url directly and use cookie for credentials
            const url = doc.getThumbnailPath(sheetNode.data, DsThumbnailSize, DsThumbnailSize);
            this.onThumbnailDownloaded(url);
        } else {
            // When not using a cookie for credentials, we have to request the thumbnails using ViewingService.
            const onReceive = (error, blob) => {

                // Display a warning if overlay thumbnail will not show up
                if (error) {
                    console.warn(`Failed to load sheet thumbnail. Error: ${error}, Urn: ${options.urn}, SheetName: ${sheetNode.name()}`);
                    return;
                }

                // assign imageUrl
                const blobUrl = av.getGlobal().URL.createObjectURL(blob);
                this.onThumbnailDownloaded(blobUrl);
            };

            // Request thumbnail from DS
            Autodesk.Viewing.Document.requestThumbnailWithSecurity(options, onReceive);
        }
    }

    // Takes a screenshot of the 2D sheet, and replace the low-res thumbnail with it.
    refineThumbnail() {

        // Make sure that we do the screenshot only once per sheetMap
        if (this.thumbnailRefined) {
            return;
        }

        this.thumbnailRefined = true;

        // Align camera to capture the whole sheet
        const box = this.viewer.model.getBoundingBox(true);

        // Setup camera for the snapshot
        const camera = new Autodesk.Viewing.Camera(GeneratedThumbnailSize, GeneratedThumbnailSize);
        camera.setViewFromBox(box, true);

        const onScreenshotDone = (imageUrl) => {
            this.img.src = imageUrl;

            this.img.onload = () => {
                if (this.viewer?.model) { // viewer or model might have been discarded in the meantime
                    const cropRegion = getThumbnailCropRegion(box, this.sheetMapConfig.viewport, this.viewer.model.getUnitScale());
                    this.cropThumbnail(this.img, cropRegion);
                }
            };
        };

        const options = {
            camera: camera
        };

        this.viewer.impl.getScreenShotProgressive(GeneratedThumbnailSize, GeneratedThumbnailSize, onScreenshotDone, options);
    }
}
